import React from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Progress } from '@/components/ui/progress';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { 
  Brain, 
  CheckCircle2, 
  AlertTriangle, 
  Clock, 
  Zap, 
  RefreshCw,
  TrendingUp,
  Eye
} from 'lucide-react';

interface ExtractionMetrics {
  totalFields: number;
  extractedFields: number;
  highConfidenceFields: number;
  lowConfidenceFields: number;
  missingFields: number;
  processingTimeMs: number;
  aiProvider: string;
  confidenceScore: number;
}

interface EnhancedExtractionProgressProps {
  metrics: ExtractionMetrics;
  isProcessing: boolean;
  onRetryExtraction?: () => void;
  onViewDetails?: () => void;
  className?: string;
}

export function EnhancedExtractionProgress({
  metrics,
  isProcessing,
  onRetryExtraction,
  onViewDetails,
  className
}: EnhancedExtractionProgressProps) {
  const completionPercentage = metrics.totalFields > 0 
    ? Math.round((metrics.extractedFields / metrics.totalFields) * 100)
    : 0;

  const getConfidenceColor = (confidence: number) => {
    if (confidence >= 0.8) return 'text-success';
    if (confidence >= 0.6) return 'text-warning';
    return 'text-destructive';
  };

  const getConfidenceBadgeVariant = (confidence: number) => {
    if (confidence >= 0.8) return 'default';
    if (confidence >= 0.6) return 'secondary';
    return 'destructive';
  };

  return (
    <Card className={className}>
      <CardHeader className="pb-3">
        <CardTitle className="flex items-center gap-2 text-lg">
          <Brain className="h-5 w-5 text-primary" />
          Extraction Progress
          {isProcessing && <RefreshCw className="h-4 w-4 animate-spin text-muted-foreground" />}
        </CardTitle>
      </CardHeader>
      
      <CardContent className="space-y-4">
        {/* Main Progress */}
        <div className="space-y-2">
          <div className="flex items-center justify-between">
            <span className="text-sm font-medium">Overall Progress</span>
            <span className="text-sm text-muted-foreground">
              {metrics.extractedFields} of {metrics.totalFields} fields
            </span>
          </div>
          <Progress value={completionPercentage} className="h-3" />
          <div className="flex justify-between text-xs text-muted-foreground">
            <span>{completionPercentage}% complete</span>
            <span className="flex items-center gap-1">
              <Clock className="h-3 w-3" />
              {metrics.processingTimeMs}ms
            </span>
          </div>
        </div>

        {/* Confidence Score */}
        <div className="flex items-center justify-between p-3 bg-muted rounded-lg">
          <div className="flex items-center gap-2">
            <Zap className="h-4 w-4 text-primary" />
            <span className="text-sm font-medium">AI Confidence</span>
          </div>
          <Badge 
            variant={getConfidenceBadgeVariant(metrics.confidenceScore)}
            className="font-mono"
          >
            {Math.round(metrics.confidenceScore * 100)}%
          </Badge>
        </div>

        {/* Field Breakdown */}
        <div className="grid grid-cols-3 gap-3">
          <div className="text-center p-2 bg-success/10 rounded-lg">
            <div className="flex items-center justify-center gap-1 mb-1">
              <CheckCircle2 className="h-4 w-4 text-success" />
              <span className="text-sm font-medium">High</span>
            </div>
            <div className="text-lg font-bold text-success">
              {metrics.highConfidenceFields}
            </div>
            <div className="text-xs text-muted-foreground">fields</div>
          </div>

          <div className="text-center p-2 bg-warning/10 rounded-lg">
            <div className="flex items-center justify-center gap-1 mb-1">
              <AlertTriangle className="h-4 w-4 text-warning" />
              <span className="text-sm font-medium">Low</span>
            </div>
            <div className="text-lg font-bold text-warning">
              {metrics.lowConfidenceFields}
            </div>
            <div className="text-xs text-muted-foreground">fields</div>
          </div>

          <div className="text-center p-2 bg-destructive/10 rounded-lg">
            <div className="flex items-center justify-center gap-1 mb-1">
              <AlertTriangle className="h-4 w-4 text-destructive" />
              <span className="text-sm font-medium">Missing</span>
            </div>
            <div className="text-lg font-bold text-destructive">
              {metrics.missingFields}
            </div>
            <div className="text-xs text-muted-foreground">fields</div>
          </div>
        </div>

        {/* AI Provider Info */}
        <div className="flex items-center justify-between text-xs text-muted-foreground">
          <span>Powered by {metrics.aiProvider}</span>
          <div className="flex items-center gap-1">
            <TrendingUp className="h-3 w-3" />
            <span>Real-time processing</span>
          </div>
        </div>

        {/* Action Buttons */}
        <div className="flex gap-2 pt-2">
          {onRetryExtraction && (
            <Button 
              variant="outline" 
              size="sm" 
              onClick={onRetryExtraction}
              disabled={isProcessing}
              className="flex-1"
            >
              <RefreshCw className="h-4 w-4 mr-2" />
              Retry Extraction
            </Button>
          )}
          {onViewDetails && (
            <Button 
              variant="outline" 
              size="sm" 
              onClick={onViewDetails}
              className="flex-1"
            >
              <Eye className="h-4 w-4 mr-2" />
              View Details
            </Button>
          )}
        </div>
      </CardContent>
    </Card>
  );
}